import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { isBankStatementItem } from "../lib/ownership";
import MonthlyCloseWidget from "../components/MonthlyCloseWidget";
import {
  CheckCircle, Clock, ArrowRight, CalendarBlank, Camera,
  ChatsCircle, ListChecks, Receipt, ShieldCheck, Question, FolderOpen, Lock, Sparkle,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import ShareWithBookkeeper from "../components/ShareWithBookkeeper";
import { toast } from "sonner";

function ymKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

export default function Dashboard() {
  const { activeCompanyId, activeCompany } = useCompany();
  const cameraRef = useRef(null);
  const [closingMetrics, setClosingMetrics] = useState(null);
  const [collectingMetrics, setCollectingMetrics] = useState(null);
  const [items, setItems] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [sweepSummary, setSweepSummary] = useState(null); // { last_auto_sweep_at, cleared_this_week, total_amount_this_week, ... }
  // In-flight photo uploads triggered from the Dashboard one-tap camera button.
  // Tracks count so the user can fire off multiple photos without waiting.
  const [uploadInflight, setUploadInflight] = useState(0);

  // Tap the camera button → opens iPhone native camera (capture="environment").
  // After photo is taken, the file is uploaded in the background. The button
  // stays active so the user can immediately tap to take another photo.
  const handleDashboardPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow same-name file twice
    if (!file || !activeCompanyId) return;
    setUploadInflight((n) => n + 1);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("company_id", activeCompanyId);
      await api.post(`/payment-advices/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadInflight((n) => Math.max(0, n - 1));
    }
  };

  const today = new Date();
  const currentYM = ymKey(today);
  const prevDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevYM = ymKey(prevDate);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    try {
      const [closing, collecting, mi, q] = await Promise.all([
        api.get(`/metrics?company_id=${activeCompanyId}&month=${prevYM}`),
        api.get(`/metrics?company_id=${activeCompanyId}&month=${currentYM}`),
        api.get(`/monthly-items?company_id=${activeCompanyId}`),
        api.get(`/questions?company_id=${activeCompanyId}&status=unresolved`),
      ]);
      setClosingMetrics(closing.data);
      setCollectingMetrics(collecting.data);
      // Hide bank-statement rows — Tessina handles those on Remittances.
      setItems((mi.data || []).filter((i) => !isBankStatementItem(i)));
      setQuestions(q.data);
    } catch (e) {
      console.error("Dashboard load failed:", e);
      toast.error("Couldn't load the dashboard. Please refresh the page.");
    }
    // Auto-sweep widget — best-effort, never blocks the rest of the dashboard.
    // 4xx is normal when QBO isn't connected; we silently skip the widget then.
    try {
      const { data } = await api.get(`/qbo/auto-sweep/summary?company_id=${activeCompanyId}`);
      setSweepSummary(data);
    } catch {
      setSweepSummary(null);
    }
  }, [activeCompanyId, prevYM, currentYM]);

  useEffect(() => { load(); }, [load]);

  if (!activeCompanyId) {
    return <div className="p-8 label-tag">No company selected</div>;
  }

  // Cross-month aggregates (advices + inquiries are not month-scoped).
  // Either response carries the same numbers — pick whichever we have.
  const xMetrics = closingMetrics || collectingMetrics;

  return (
    <div data-testid="dashboard-page" className="p-6 sm:p-8 max-w-[1600px]">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="label-tag mb-2 flex items-center gap-2">
            <CalendarBlank size={12} weight="duotone" /> Today is {today.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] break-words">
            {activeCompany?.name || "Overview"}
          </h1>
          <p className="text-[#525252] mt-3 max-w-2xl text-sm sm:text-base">
            Books usually finish mid-next-month, so we show two periods: the one we&rsquo;re closing, and the one we&rsquo;re still collecting.
          </p>
        </div>
        <div className="hidden lg:block">
          <ShareWithBookkeeper />
        </div>
      </div>

      {/* AI auto-sweep activity — replaces the bookkeeper's UF reconciliation
          chore. Only rendered when QBO is connected AND the system has actually
          auto-swept at least once for this company. Lives directly under the
          header so the owner sees the AI's contribution before scrolling. */}
      {sweepSummary && (sweepSummary.last_auto_sweep_at || sweepSummary.cleared_this_week > 0) && (
        <AutoSweepWidget data={sweepSummary} />
      )}

      {/* Quick actions (mobile-first) */}
      <div className="grid grid-cols-2 gap-3 mb-6 lg:hidden">
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleDashboardPhoto}
          data-testid="dashboard-camera-input"
        />
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          data-testid="quick-snap-advice"
          className="btn-ikb px-4 py-3 flex items-center justify-center gap-2 text-sm relative"
        >
          <Camera size={16} weight="bold" />
          <span>{uploadInflight > 0 ? "Take another" : "Snap advice"}</span>
          {uploadInflight > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-[#10B981] text-white text-[10px] font-bold rounded-full inline-flex items-center justify-center"
              data-testid="dashboard-upload-badge"
              title={`${uploadInflight} photo${uploadInflight === 1 ? "" : "s"} uploading in the background`}
            >
              {uploadInflight}
            </span>
          )}
        </button>
        <div data-testid="mobile-share-slot">
          <ShareWithBookkeeper variant="compact" />
        </div>
      </div>

      {/* Older-inquiries banner — surfaces stuff from prior close periods so it doesn't get forgotten */}
      <OlderInquiriesBanner metrics={xMetrics} />

      {/* SECTION 1 — Closing previous month (urgent visual style) */}
      <PeriodSection
        kind="closing"
        title={`Closing — ${monthLabel(prevYM)}`}
        subtitle="Where the active work is."
        icon={Lock}
        metrics={closingMetrics}
        targetDay={activeCompany?.close_target_day}
        forMonth={prevYM}
        testid="period-closing"
      />

      {/* The close blockers widget belongs visually with the closing section */}
      <MonthlyCloseWidget companyId={activeCompanyId} />

      {/* SECTION 2 — Collecting current month (calmer visual style) */}
      <PeriodSection
        kind="collecting"
        title={`Collecting — ${monthLabel(currentYM)}`}
        subtitle="Statements still arriving — no rush yet."
        icon={FolderOpen}
        metrics={collectingMetrics}
        forMonth={currentYM}
        testid="period-collecting"
      />

      {/* SECTION 3 — Open across all months (advices + older inquiries) */}
      {xMetrics && (
        <CrossMonthSection metrics={xMetrics} questions={questions} />
      )}

      {/* Two real lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Open inquiries */}
        <div className="lg:col-span-2 surface-card bg-white">
          <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0">
                <ChatsCircle size={18} weight="duotone" className="text-[#EF4444]" />
              </div>
              <div>
                <div className="label-tag mb-0.5">Open Inquiries</div>
                <h2 className="font-heading text-xl font-medium tracking-tight">
                  Awaiting your response
                </h2>
              </div>
            </div>
            <Link
              to="/questions"
              data-testid="view-all-questions-link"
              className="text-xs label-tag text-[#002FA7] hover:underline flex items-center gap-1"
            >
              View all <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="divide-y divide-[#E5E5E5]">
            {questions.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle size={32} weight="duotone" className="mx-auto text-[#10B981] mb-2" />
                <div className="font-heading text-lg">No outstanding inquiries.</div>
                <div className="text-sm text-[#525252]">All questions have been resolved for this entity.</div>
              </div>
            ) : (
              questions.slice(0, 5).map((q) => (
                <Link
                  to="/questions"
                  key={q.id}
                  data-testid={`dashboard-question-${q.id}`}
                  className="block p-4 hover:bg-[#FAFAFA] transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#EF4444]/10 flex items-center justify-center flex-shrink-0">
                      <Question size={16} weight="fill" className="text-[#EF4444]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-base font-medium leading-snug mb-0.5">{q.title}</div>
                      {q.body && <div className="text-sm text-[#525252] line-clamp-2">{q.body}</div>}
                      <div className="label-tag mt-2 text-[10px]">
                        from {q.created_by_name} · {new Date(q.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>

        {/* Still needed */}
        <div className="surface-card bg-white">
          <div className="p-5 border-b border-[#E5E5E5] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                <ListChecks size={18} weight="duotone" className="text-[#F59E0B]" />
              </div>
              <div>
                <div className="label-tag mb-0.5">Monthly Items</div>
                <h2 className="font-heading text-xl font-medium tracking-tight">Still needed</h2>
              </div>
            </div>
            <Link
              to="/recurring"
              data-testid="view-all-recurring-link"
              className="text-xs label-tag text-[#002FA7] hover:underline flex items-center gap-1"
            >
              View all <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div>
            {(() => {
              const outstanding = items.filter((i) => i.status !== "submitted");
              if (items.length === 0) {
                return <div className="p-6 text-sm text-[#525252]">No recurring items configured.</div>;
              }
              if (outstanding.length === 0) {
                return (
                  <div className="p-8 text-center">
                    <CheckCircle size={32} weight="duotone" className="mx-auto text-[#10B981] mb-2" />
                    <div className="font-heading text-lg">Nothing outstanding.</div>
                    <div className="text-sm text-[#525252]">Every recurring item has been submitted for this month.</div>
                  </div>
                );
              }
              return outstanding.slice(0, 8).map((i) => {
                const isAwaiting = i.status === "awaiting_approval";
                const isDecision = i.category === "decision";
                const iconBg = isAwaiting ? "bg-[#DC2626]/10" : isDecision ? "bg-[#002FA7]/10" : "bg-[#F59E0B]/10";
                const iconColor = isAwaiting ? "text-[#DC2626]" : isDecision ? "text-[#002FA7]" : "text-[#F59E0B]";
                const Icon = isAwaiting ? Question : Clock;
                return (
                  <div
                    key={i.id}
                    data-testid={`dashboard-item-${i.id}`}
                    className="px-4 py-3 border-b border-[#E5E5E5] last:border-b-0 flex items-center gap-3"
                  >
                    <div className={`w-8 h-8 ${iconBg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={14} weight={isAwaiting ? "fill" : "duotone"} className={iconColor} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-base font-medium leading-snug truncate">{i.name}</div>
                      <div className="text-[12px] text-[#525252] truncate">
                        {isAwaiting ? (
                          <>Awaiting your decision{i.tax_amount !== undefined && i.tax_amount !== null ? ` · $${Number(i.tax_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ""}</>
                        ) : (
                          i.vendor || ""
                        )}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// PeriodSection — section header + 3 tiles for a month (Statements / Inquiries / Close).
// `kind` controls visual urgency: closing = urgent colors; collecting = calm/muted.
// ─────────────────────────────────────────────────────────────────────────
function PeriodSection({ kind, title, subtitle, icon: HeaderIcon, metrics, targetDay, forMonth, testid }) {
  if (!metrics) return null;
  const items = metrics.recurring || {};
  const itemsTotal = items.total || 0;
  const itemsTodo = (items.pending || 0) + (items.awaiting_approval || 0);
  const inq = metrics.questions || {};
  const inqOpenForMonth = inq.directed_to_me_for_month ?? inq.unresolved_for_month ?? 0;
  const inqTotalForMonth = inq.total_for_month || 0;

  const isClosing = kind === "closing";
  const labelHint =
    itemsTotal === 0 ? "no statements scheduled" :
    itemsTodo === 0 ? (isClosing ? "all submitted" : "all in early") :
    isClosing ? "to upload" : "expected";
  const stmtTone =
    itemsTotal === 0 ? "neutral" :
    itemsTodo === 0 ? "ok" :
    isClosing ? (itemsTodo > 5 ? "warn" : "amber") : "calm";

  const inqHint =
    inqOpenForMonth > 0 ? "to answer" :
    inqTotalForMonth === 0 ? "none yet" :
    "all answered";
  const inqTone =
    inqTotalForMonth === 0 ? (isClosing ? "neutral" : "calm") :
    inqOpenForMonth === 0 ? "ok" :
    isClosing ? (inqOpenForMonth > 3 ? "warn" : "amber") : "calm";

  const accent = isClosing ? "#0A0A0A" : "#737373";
  const subAccent = isClosing ? "text-[#525252]" : "text-[#A3A3A3]";

  return (
    <section className="mb-8" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${isClosing ? "bg-[#0A0A0A] text-white" : "bg-[#E5E5E5] text-[#525252]"}`}>
          <HeaderIcon size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-tag" style={{ color: accent }}>{isClosing ? "Active period" : "Next period"}</div>
          <h2 className="font-heading text-xl sm:text-2xl font-medium tracking-tight" style={{ color: accent }}>
            {title}
          </h2>
        </div>
        <div className={`text-sm hidden sm:block ${subAccent}`}>{subtitle}</div>
      </div>

      {/* Close target progress (closing period only, when configured) */}
      {isClosing && targetDay && (
        <CloseTargetIndicator forMonth={forMonth} targetDay={targetDay} />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <BigNumberTile
          to="/recurring"
          icon={ListChecks}
          label="Statements"
          value={itemsTotal === 0 ? "—" : itemsTodo}
          hint={labelHint}
          tone={stmtTone}
          dimmed={!isClosing}
          testid={`${testid}-statements`}
        />
        <BigNumberTile
          to="/questions"
          icon={ChatsCircle}
          label="Inquiries for me"
          value={inqOpenForMonth}
          hint={inqHint}
          tone={inqTone}
          dimmed={!isClosing}
          testid={`${testid}-inquiries`}
        />
        <BigNumberTile
          to="/close"
          icon={ShieldCheck}
          label={isClosing ? "Close status" : "Close in waiting"}
          value={itemsTotal === 0 ? "—" : itemsTodo === 0 && inqOpenForMonth === 0 ? "Ready" : "Open"}
          hint={
            itemsTotal === 0 ? "no items" :
            itemsTodo === 0 && inqOpenForMonth === 0 ? "ready to sign off" :
            isClosing ? "work remaining" : "starts after collection"
          }
          tone={
            itemsTotal === 0 ? "neutral" :
            itemsTodo === 0 && inqOpenForMonth === 0 ? "ok" :
            isClosing ? "amber" : "calm"
          }
          isStatus
          dimmed={!isClosing}
          testid={`${testid}-close`}
        />
      </div>
    </section>
  );
}

// Soft progress indicator for the configured close target — gradually shifts
// blue → amber → red as the target date approaches/passes. Non-blocking, no nagging.
function CloseTargetIndicator({ forMonth, targetDay }) {
  if (!forMonth || !targetDay) return null;
  // Target date is in the month AFTER the closing month — i.e. for "Closing March 2026"
  // the target lives in April. When today >= target → red overdue.
  const [y, m] = forMonth.split("-").map(Number);
  const target = new Date(y, m, Math.min(targetDay, new Date(y, m + 1, 0).getDate()));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / 86400000);
  const overdue = diffDays < 0;
  const soon = diffDays >= 0 && diffDays <= 3;
  const upcoming = diffDays >= 4 && diffDays <= 14;
  const tone = overdue ? "red" : soon ? "amber" : upcoming ? "amber" : "blue";
  const palette = {
    blue:  { bg: "bg-[#002FA7]/[0.06]", border: "border-[#002FA7]/40", text: "text-[#002FA7]", bar: "bg-[#002FA7]" },
    amber: { bg: "bg-[#F59E0B]/[0.10]", border: "border-[#F59E0B]/50", text: "text-[#92400E]", bar: "bg-[#F59E0B]" },
    red:   { bg: "bg-[#EF4444]/[0.10]", border: "border-[#EF4444]/50", text: "text-[#991B1B]", bar: "bg-[#EF4444]" },
  }[tone];

  // Progress: window of 30 days before target; today's position within that window
  const total = 30;
  const elapsed = Math.max(0, Math.min(total, total - diffDays));
  const pct = Math.round((elapsed / total) * 100);

  const label = overdue ? `Close target was ${target.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? "" : "s"} past`
    : diffDays === 0 ? `Close target is today (${target.toLocaleDateString("en-US", { month: "short", day: "numeric" })})`
    : `Close target: ${target.toLocaleDateString("en-US", { month: "short", day: "numeric" })} — ${diffDays} day${diffDays === 1 ? "" : "s"} away`;

  return (
    <div className={`mb-4 px-4 py-2.5 border ${palette.border} ${palette.bg}`} data-testid="close-target-indicator">
      <div className="flex items-center gap-3">
        <CalendarBlank size={14} weight="duotone" className={palette.text} />
        <div className={`text-sm font-medium ${palette.text} flex-1`}>{label}</div>
      </div>
      <div className="mt-2 h-1.5 bg-white/60 overflow-hidden">
        <div className={`h-full ${palette.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// CrossMonthSection — remittances + older-month inquiries (don't belong to either period).
// ─────────────────────────────────────────────────────────────────────────
function CrossMonthSection({ metrics, questions }) {
  const advices = metrics.payment_advices || {};
  const inq = metrics.questions || {};
  const advicesPending = advices.pending || 0;
  const advicesMatched = advices.matched || 0;
  const advicesTotal = advices.total || 0;
  const olderUnresolved = inq.older_unresolved || 0;
  const allOpen = (questions && questions.length) || inq.unresolved || 0;

  const advicesHint =
    advicesPending > 0 ? "to match" :
    advicesTotal === 0 ? "none yet" :
    advicesMatched > 0 ? "all matched" :
    "all matched";
  const advicesTone = advicesTotal === 0 ? "neutral" : advicesPending > 0 ? (advicesPending > 5 ? "warn" : "amber") : "ok";

  const olderHint =
    olderUnresolved === 0 ? "nothing forgotten" :
    olderUnresolved > 0 ? "from prior periods" :
    "nothing forgotten";
  const olderTone = olderUnresolved === 0 ? "ok" : olderUnresolved > 2 ? "warn" : "amber";

  return (
    <section className="mb-10" data-testid="period-open-across">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 flex items-center justify-center flex-shrink-0 bg-[#0A0A0A] text-white">
          <ListChecks size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-tag">Open across all months</div>
          <h2 className="font-heading text-xl sm:text-2xl font-medium tracking-tight">
            Remittances &amp; older inquiries
          </h2>
        </div>
        <div className="text-[11px] text-[#A3A3A3] hidden sm:block">
          all open: {allOpen}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <BigNumberTile
          to="/payment-advices"
          icon={Receipt}
          label="Remittances"
          value={advicesPending}
          hint={advicesHint}
          tone={advicesTone}
          testid="x-remittances"
        />
        <BigNumberTile
          to="/questions"
          icon={ChatsCircle}
          label="Older inquiries"
          value={olderUnresolved}
          hint={olderHint}
          tone={olderTone}
          testid="x-older-inquiries"
        />
      </div>
    </section>
  );
}

// Loud banner only when there are stale (older) inquiries — keeps them from being forgotten.
function OlderInquiriesBanner({ metrics }) {
  const olderUnresolved = metrics?.questions?.older_unresolved || 0;
  if (olderUnresolved === 0) return null;
  return (
    <Link
      to="/questions"
      data-testid="older-inquiries-banner"
      className="mb-6 flex items-center gap-3 px-4 py-3 border-l-4 border-[#EF4444] bg-[#EF4444]/[0.08] hover:bg-[#EF4444]/[0.12] transition-all group"
    >
      <Question size={20} weight="fill" className="text-[#EF4444] flex-shrink-0" />
      <div className="flex-1 text-sm">
        <div className="font-medium text-[#991B1B]">
          {olderUnresolved} inquir{olderUnresolved === 1 ? "y" : "ies"} from prior close periods still open.
        </div>
        <div className="text-[#7F1D1D] text-[12px]">Review them so they don&rsquo;t get forgotten.</div>
      </div>
      <ArrowRight size={16} weight="bold" className="text-[#7F1D1D] group-hover:translate-x-0.5 transition-transform" />
    </Link>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// BigNumberTile — single big-number card. `dimmed` makes the calm/collecting style.
// ─────────────────────────────────────────────────────────────────────────
function BigNumberTile({ to, icon: Icon, label, value, hint, tone, isStatus, dimmed, testid }) {
  const palettes = {
    ok:      { dot: "bg-[#10B981]", border: "border-[#10B981]/40 hover:border-[#10B981]", bg: "bg-[#10B981]/[0.04]", text: "text-[#10B981]" },
    amber:   { dot: "bg-[#F59E0B]", border: "border-[#F59E0B]/40 hover:border-[#F59E0B]", bg: "bg-[#F59E0B]/[0.06]", text: "text-[#F59E0B]" },
    warn:    { dot: "bg-[#EF4444]", border: "border-[#EF4444]/40 hover:border-[#EF4444]", bg: "bg-[#EF4444]/[0.06]", text: "text-[#EF4444]" },
    neutral: { dot: "bg-[#A3A3A3]", border: "border-[#E5E5E5] hover:border-[#0A0A0A]", bg: "bg-white", text: "text-[#525252]" },
    calm:    { dot: "bg-[#A3A3A3]", border: "border-[#E5E5E5] hover:border-[#737373]", bg: "bg-[#FAFAFA]", text: "text-[#525252]" },
  };
  const p = palettes[tone] || palettes.neutral;

  return (
    <Link
      to={to}
      data-testid={testid}
      className={`p-5 sm:p-6 border-2 ${p.border} ${p.bg} flex flex-col gap-3 transition-all ${dimmed ? "min-h-[140px]" : "min-h-[160px]"}`}
    >
      <div className="flex items-center justify-between">
        <div className="label-tag text-[11px] tracking-widest">{label}</div>
        <div className="relative">
          <Icon size={20} weight="duotone" className={dimmed ? "text-[#737373]" : "text-[#0A0A0A]"} />
          <span className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-white ${p.dot}`} />
        </div>
      </div>
      <div
        className={`font-heading font-medium leading-none ${p.text} ${
          isStatus ? "text-3xl sm:text-4xl" : (dimmed ? "text-4xl sm:text-5xl" : "text-5xl sm:text-6xl")
        }`}
        data-testid={`${testid}-value`}
      >
        {value}
      </div>
      <div className={`text-sm mt-auto ${dimmed ? "text-[#737373]" : "text-[#525252]"}`}>{hint}</div>
    </Link>
  );
}

// Format an ISO timestamp into a casual relative string like "5 min ago" or "2 hours ago".
// Falls back to a short date for anything older than ~7 days.
function relativeTime(isoStr) {
  if (!isoStr) return "";
  const then = new Date(isoStr);
  if (Number.isNaN(then.getTime())) return "";
  const diffMs = Date.now() - then.getTime();
  const sec = Math.max(0, Math.round(diffMs / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? "" : "s"} ago`;
  return then.toLocaleDateString();
}

function AutoSweepWidget({ data }) {
  const week = data.cleared_this_week || 0;
  const month = data.cleared_this_month || 0;
  const weekAmt = data.total_amount_this_week || 0;
  const lastWhen = relativeTime(data.last_auto_sweep_at);

  return (
    <Link
      to="/payment-advices"
      data-testid="dashboard-auto-sweep-widget"
      className="block mb-6 border border-[#10B981]/40 bg-gradient-to-r from-[#10B981]/[0.08] to-[#10B981]/[0.04] hover:border-[#10B981] hover:from-[#10B981]/[0.14] transition-colors"
    >
      <div className="px-4 py-3 sm:px-5 sm:py-4 flex items-center gap-3 sm:gap-4">
        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-[#10B981]/15 flex items-center justify-center">
          <Sparkle size={20} weight="duotone" className="text-[#10B981]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.18em] uppercase font-semibold text-[#10B981]/80">
            AI auto-sweep
          </div>
          <div className="text-sm sm:text-base text-[#171717] mt-0.5 font-medium leading-snug">
            {lastWhen ? (
              <>
                <span>Last auto-sweep: <span className="text-[#065F46] font-semibold">{lastWhen}</span></span>
                {week > 0 && (
                  <>
                    {" — "}
                    <span className="text-[#065F46] font-semibold">{week}</span>
                    {" deposit"}{week === 1 ? "" : "s"} cleared this week
                  </>
                )}
              </>
            ) : (
              <span className="text-[#525252]">Auto-sweep is on. Nothing to clear in the last 7 days.</span>
            )}
          </div>
          {(week > 0 || month > 0) && (
            <div className="text-xs text-[#525252] mt-0.5">
              {weekAmt > 0 && (
                <>
                  ${weekAmt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {" "}swept this week
                </>
              )}
              {month > 0 && month !== week && (
                <>
                  {weekAmt > 0 ? " · " : ""}
                  <span>{month} deposits / ${data.total_amount_this_month.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} in last 30 days</span>
                </>
              )}
            </div>
          )}
        </div>
        <ArrowRight size={16} weight="bold" className="text-[#10B981] flex-shrink-0" />
      </div>
    </Link>
  );
}

